import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import { UTCDate } from "@date-fns/utc";
import { formatDistanceToNow, startOfDay } from "date-fns";
import { debounce } from "lodash";

import { Input } from "@components/ui/Input";

import { Loading } from "@pages/v2/components/layout/Loading";
import { DatePicker } from "@pages/v2/components/ui/datepicker/DatePicker";
import { Pagination } from "@pages/v2/components/ui/pagination/Pagination";
import { SortTable, TableSort } from "@pages/v2/components/ui/table/SortTable";
import { Table } from "@pages/v2/components/ui/table/Table";
import {
  formatAmount,
  formatNullableDate,
  isLegacyRow,
  LEGACY_BADGE_CLASS,
  statusBadgeClass,
  transactionDetailPath,
} from "@pages/v2/queries/transactions/format";
import { searchTransactions } from "@pages/v2/queries/transactions/transactionQueries";
import type {
  TransactionListResponse,
  TransactionStatus,
} from "@pages/v2/queries/transactions/types";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

const STATUS_OPTIONS: ReadonlyArray<{
  value: TransactionStatus;
  label: string;
}> = [
  { value: "authorized", label: "Authorized" },
  { value: "settled", label: "Settled" },
  { value: "voided", label: "Voided" },
  { value: "settlement_declined", label: "Settlement declined" },
  { value: "expired", label: "Expired" },
  { value: "gateway_rejected", label: "Gateway rejected" },
  { value: "refunded", label: "Refunded" },
  // Imported Broadleaf-era rows (OMG-2574). "Settled" above covers their
  // settled purchases too; these two are the legacy-only outcomes.
  { value: "legacy_unsettled", label: "Legacy: never settled" },
  { value: "legacy_unknown", label: "Legacy: no payment record" },
];

type Filters = {
  statuses: TransactionStatus[];
  email: string;
  authorizedFrom: Date | null;
  authorizedTo: Date | null;
  pageSize: number;
  page: number;
  sortOrder: "asc" | "desc";
};

// URL is the single source of truth. All filter/page state goes through
// useSearchParams so reload, share, and back/forward all behave naturally.
const parseFilters = (params: URLSearchParams): Filters => {
  const rawStatuses = params.get("status") ?? "";
  const statuses = rawStatuses
    .split(",")
    .map((s) => s.trim())
    .filter((s): s is TransactionStatus =>
      STATUS_OPTIONS.some((o) => o.value === s),
    );

  // Day filters are viewer-independent (OMG-2324): the URL stores plain
  // YYYY-MM-DD, anchored in UTC. startOfDay is only for legacy shared links
  // that stored a full ISO instant — it snaps them to the instant's UTC day.
  const parseDay = (raw: string | null): Date | null => {
    if (!raw) return null;
    const date = new UTCDate(raw);
    return Number.isNaN(date.getTime()) ? null : startOfDay(date);
  };

  const pageSizeRaw = Number(params.get("pageSize"));
  const pageSize = PAGE_SIZE_OPTIONS.includes(
    pageSizeRaw as (typeof PAGE_SIZE_OPTIONS)[number],
  )
    ? pageSizeRaw
    : DEFAULT_PAGE_SIZE;

  const pageRaw = Number(params.get("page"));
  const page = Number.isFinite(pageRaw) && pageRaw >= 1 ? pageRaw : 1;

  const sortRaw = params.get("sortOrder");
  const sortOrder: "asc" | "desc" = sortRaw === "asc" ? "asc" : "desc";

  return {
    statuses,
    email: params.get("email") ?? "",
    authorizedFrom: parseDay(params.get("authorizedFrom")),
    authorizedTo: parseDay(params.get("authorizedTo")),
    pageSize,
    page,
    sortOrder,
  };
};

const AllTransactions = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const [data, setData] = useState<TransactionListResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  // Local state for the email input so typing feels responsive; URL is updated
  // on a 500ms debounce, which then triggers the refetch via searchParams.
  const [emailInput, setEmailInput] = useState(filters.email);
  // Keep the input in sync if the URL changes externally (back/forward).
  useEffect(() => {
    setEmailInput(filters.email);
  }, [filters.email]);

  // Mutate one or more params while preserving the rest. Filter changes reset
  // page back to 1 (results shift); sort and page changes pass resetPage:false.
  const updateParams = (
    mutate: (next: URLSearchParams) => void,
    options: { resetPage?: boolean } = { resetPage: true },
  ) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      mutate(next);
      if (options.resetPage !== false) next.delete("page");
      return next;
    });
  };

  const debouncedSetEmail = useRef(
    debounce((email: string) => {
      updateParams((next) => {
        if (email) next.set("email", email);
        else next.delete("email");
      });
    }, 500),
  ).current;

  useEffect(() => () => debouncedSetEmail.cancel(), [debouncedSetEmail]);

  useEffect(() => {
    // Dates travel as plain calendar days (OMG-2324): the API owns the day
    // semantics and brackets them as UTC day boundaries, so a shared link
    // returns identical rows in PT and ET.
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    searchTransactions({
      statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
      email: filters.email || undefined,
      authorizedFrom: filters.authorizedFrom
        ? filters.authorizedFrom.toISOString().slice(0, 10)
        : null,
      authorizedTo: filters.authorizedTo
        ? filters.authorizedTo.toISOString().slice(0, 10)
        : null,
      limit: filters.pageSize,
      offset: (filters.page - 1) * filters.pageSize,
      sortOrder: filters.sortOrder,
    })
      .then((res) => {
        // Drop stale responses: rapid filter changes can race, and a slower
        // older fetch resolving last would otherwise overwrite the table
        // with results that don't match the visible filters.
        if (!cancelled) setData(res);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    filters.statuses,
    filters.email,
    filters.authorizedFrom,
    filters.authorizedTo,
    filters.pageSize,
    filters.page,
    filters.sortOrder,
  ]);

  const handleStatusChange = (
    selected: ReadonlyArray<{ value: TransactionStatus; label: string }>,
  ) => {
    updateParams((next) => {
      if (selected.length === 0) next.delete("status");
      else next.set("status", selected.map((s) => s.value).join(","));
    });
  };

  const handleDateChange = (
    field: "authorizedFrom" | "authorizedTo",
    date: Date | null | undefined,
  ) => {
    updateParams((next) => {
      // Serialize the picked day, never an instant — the same URL must mean
      // the same day for every viewer (OMG-2324). The picker runs with
      // timeZone="UTC", so the selected date is a UTC midnight.
      if (date) next.set(field, date.toISOString().slice(0, 10));
      else next.delete(field);
    });
  };

  const handlePageSizeChange = (size: number) => {
    updateParams((next) => {
      if (size === DEFAULT_PAGE_SIZE) next.delete("pageSize");
      else next.set("pageSize", String(size));
    });
  };

  const handleSortToggle = () => {
    const nextOrder = filters.sortOrder === "asc" ? "desc" : "asc";
    updateParams(
      (next) => {
        if (nextOrder === "desc") next.delete("sortOrder");
        else next.set("sortOrder", "asc");
      },
      { resetPage: false },
    );
  };

  const handlePageChange = (page: number) => {
    updateParams(
      (next) => {
        if (page === 1) next.delete("page");
        else next.set("page", String(page));
      },
      { resetPage: false },
    );
  };

  const handleClearFilters = () => {
    setEmailInput("");
    debouncedSetEmail.cancel();
    setSearchParams(new URLSearchParams());
  };

  const totalPages = data
    ? Math.max(1, Math.ceil(data.total / filters.pageSize))
    : 1;
  const hasActiveFilter =
    filters.statuses.length > 0 ||
    !!filters.email ||
    !!filters.authorizedFrom ||
    !!filters.authorizedTo;

  const selectedStatusOptions = STATUS_OPTIONS.filter((o) =>
    filters.statuses.includes(o.value),
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <div className="lg:col-span-2">
          <label className="block text-xs text-gray-500 mb-1">Email</label>
          <Input
            type="search"
            placeholder="Search by email..."
            value={emailInput}
            onChange={(e) => {
              setEmailInput(e.target.value);
              debouncedSetEmail(e.target.value);
            }}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <Select
            isMulti
            options={STATUS_OPTIONS}
            value={selectedStatusOptions}
            onChange={(selected) => handleStatusChange(selected ?? [])}
            placeholder="Any status"
            classNames={{ control: () => "min-h-10" }}
          />
        </div>
        <div className="flex gap-2">
          <DatePicker
            label="From"
            name="authorizedFrom"
            selected={filters.authorizedFrom ?? undefined}
            onSelect={(date) => handleDateChange("authorizedFrom", date)}
            placeholder="From..."
            register={() => ({ name: "authorizedFrom" })}
            timeZone="UTC"
          />
          <DatePicker
            label="To"
            name="authorizedTo"
            selected={filters.authorizedTo ?? undefined}
            onSelect={(date) => handleDateChange("authorizedTo", date)}
            placeholder="To..."
            register={() => ({ name: "authorizedTo" })}
            timeZone="UTC"
          />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-sm text-gray-600">
          <label className="flex items-center gap-2">
            Per page:
            <select
              className="border rounded-md px-2 py-1 text-sm"
              value={filters.pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
          {data && (
            <span>
              {data.total.toLocaleString()} result
              {data.total === 1 ? "" : "s"}
            </span>
          )}
        </div>
        {hasActiveFilter && (
          <button
            // bg-transparent (idle AND hover) opts out of the global button
            // skin from AdminPortal.module.css (an unscoped element selector
            // that paints every non-header button bg-blue-600 with
            // hover:bg-blue-700 — same blue as this text, which made the
            // label invisible).
            className="bg-transparent hover:bg-transparent text-sm text-blue-600 hover:underline"
            onClick={handleClearFilters}
          >
            Clear filters
          </button>
        )}
      </div>

      {error ? (
        <div className="rounded-md bg-red-50 p-4 text-red-700 text-sm">
          Failed to load transactions: {error}
        </div>
      ) : loading || !data ? (
        <Loading />
      ) : data.transactions.length === 0 ? (
        <div className="rounded-md bg-gray-50 p-8 text-center text-gray-600">
          <p className="font-medium">No transactions match these filters.</p>
        </div>
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Email</th>
                <th>Amount</th>
                <th>Status</th>
                <th className="cursor-pointer" onClick={handleSortToggle}>
                  <SortTable
                    title="Authorized"
                    direction={
                      filters.sortOrder === "asc"
                        ? TableSort.ASC
                        : TableSort.DESC
                    }
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {data.transactions.map((row) => {
                // Legacy rows (OMG-2574) open their own read-only detail
                // page; live rows open the pending_settlement one. A gift id
                // can appear on both purchaser and recipient, hence the
                // composite key.
                const legacy = isLegacyRow(row);
                return (
                  <tr
                    key={`${row.source}-${row.transactionId}-${row.customerId ?? row.email}`}
                    className="cursor-pointer"
                    onClick={() => navigate(transactionDetailPath(row))}
                  >
                    <td>
                      {row.transactionId}
                      {legacy && (
                        <span className={LEGACY_BADGE_CLASS}>legacy</span>
                      )}
                    </td>
                    <td>{row.email}</td>
                    <td>{formatAmount(row.amount, row.currency)}</td>
                    <td>
                      <span className={statusBadgeClass(row.status)}>
                        {row.status}
                      </span>
                    </td>
                    <td>
                      {formatNullableDate(row.authorizedAt, (date) =>
                        formatDistanceToNow(date, { addSuffix: true }),
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Pagination
            currentPage={filters.page}
            totalPages={totalPages}
            setCurrentPage={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

export default AllTransactions;
